import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useLang } from "@/i18n";

export default function InvoiceTextsDialog({ invoice, onClose, onSaved }) {
  const { t } = useLang();
  const [descs, setDescs] = useState([]);
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!invoice) return;
    setDescs((invoice.lines || []).map((l) => l.description || ""));
    setRemark(invoice.remark || "");
  }, [invoice]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/invoices/${invoice.id}/texts`, {
        lines: descs.map((description, index) => ({ index, description })),
        remark,
      });
      toast.success(t("invoiceTextsSaved"));
      onSaved?.(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="invoice-texts-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("invoiceTextsTitle")} · <span className="font-mono text-primary">{invoice?.invoice_number}</span></DialogTitle>
          <DialogDescription>{t("invoiceTextsHint")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(invoice?.lines || []).map((l, i) => (
            <div key={i} className="rounded-md border border-border bg-muted/30 p-3 space-y-1.5" data-testid={`invoice-line-${i}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium truncate">{l.name}</span>
                <span className="font-mono text-xs text-muted-foreground">{l.quantity} × {Number(l.unit_price).toFixed(2)} €</span>
              </div>
              <Input value={descs[i] ?? ""} placeholder={t("lineDescriptionPlaceholder")} maxLength={500}
                onChange={(e) => setDescs((d) => d.map((x, j) => (j === i ? e.target.value : x)))}
                data-testid={`invoice-line-desc-${i}`} />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{t("invoiceRemark")}</Label>
            <Textarea value={remark} rows={4} maxLength={1500} placeholder={t("invoiceRemarkPlaceholder")}
              onChange={(e) => setRemark(e.target.value)} data-testid="invoice-remark-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="invoice-texts-cancel">{t("cancel")}</Button>
          <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={save} disabled={saving} data-testid="invoice-texts-save">{saving ? t("loading") : t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
